import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, ArrowRight, CircleCheck, OctagonAlert, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * r16sd (design 5.3): the dashboard's "Needs attention" tile - how many slots something holds back or that have settings to check,
 * out of all of them, from the slot list's `attention` (no request of its own). A part-to-whole meter (urgent = a red hold, to check =
 * amber or findings only, the rest the neutral track; 2px gaps, icon + label + count in the legend so colour is never alone) and the
 * top reasons as a bar list in the server's own words, each linking to the filtered Slots page. Drawn only while the doctor is on.
 */
const fmt = (v) => Number(v || 0).toLocaleString();
const pct = (n, total) => (total > 0 ? Math.max(0, Math.min(100, (n / total) * 100)) : 0);

function Segment({ width, cls, label, count, total, testid, onHover, active }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn('relative h-full min-w-[6px] first:rounded-l-[4px] last:rounded-r-[4px] transition-opacity', cls, active === false && 'opacity-40')}
      style={{ width: `${width}%` }}
      onMouseEnter={() => onHover(testid)}
      onMouseLeave={() => onHover(null)}
      data-testid={testid}
      aria-label={`${label}: ${fmt(count)} of ${fmt(total)} slots`}
      role="img"
    />
  );
}

export default function AttentionTile({ summary, to = '/slots?attention=1', testid = 'tile-attention' }) {
  const [hover, setHover] = useState(null);
  if (!summary) return null;
  const { total, slots, red, amber, clear, reasons } = summary;
  const top = reasons.slice(0, 4);
  const maxCount = top.reduce((m, r) => Math.max(m, r.count), 0);
  const tip = hover === `${testid}-red` ? ['Urgent', red] : hover === `${testid}-amber` ? ['To check', amber]
    : hover === `${testid}-clear` ? ['Clear', clear] : null;

  return (
    <section className="aurora-card p-5 sm:p-6" data-testid={testid} aria-label="Needs attention">
      <div className="flex flex-col gap-5 lg:flex-row lg:gap-8">
        {/* the figure + the meter */}
        <div className="min-w-0 lg:w-[22rem] lg:shrink-0">
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <Stethoscope className="h-4 w-4 text-primary" aria-hidden />
              Needs attention
            </p>
            {slots > 0 && (
              <Link to={to} className="tap inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                data-testid={`${testid}-link`}>
                View slots <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            )}
          </div>
          <p className="mt-3 flex flex-wrap items-baseline gap-x-2 leading-none">
            <span className="font-heading text-4xl font-bold tracking-tight" data-testid={`${testid}-value`}>{fmt(slots)}</span>
            <span className="text-base font-medium text-muted-foreground/80">of {fmt(total)} slot{total === 1 ? '' : 's'}</span>
          </p>
          <div className="relative mt-4">
            <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-[4px] bg-secondary" data-testid={`${testid}-meter`}>
              <Segment width={pct(red, total)} cls="bg-danger" label="Urgent" count={red} total={total} testid={`${testid}-red`}
                onHover={setHover} active={hover ? hover === `${testid}-red` : undefined} />
              <Segment width={pct(amber, total)} cls="bg-warning" label="To check" count={amber} total={total} testid={`${testid}-amber`}
                onHover={setHover} active={hover ? hover === `${testid}-amber` : undefined} />
              <Segment width={pct(clear, total)} cls="bg-muted-foreground/25" label="Clear" count={clear} total={total} testid={`${testid}-clear`}
                onHover={setHover} active={hover ? hover === `${testid}-clear` : undefined} />
            </div>
            {tip && (
              <div className="pointer-events-none absolute -top-9 left-0 rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-lg" role="tooltip">
                <span className="font-medium">{tip[0]}</span> <span className="tnum text-muted-foreground">{fmt(tip[1])} · {Math.round(pct(tip[1], total))}%</span>
              </div>
            )}
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs" data-testid={`${testid}-legend`}>
            <li className="inline-flex items-center gap-1.5"><OctagonAlert className="h-3.5 w-3.5 text-danger" aria-hidden />Urgent <span className="font-semibold tnum">{fmt(red)}</span></li>
            <li className="inline-flex items-center gap-1.5"><TriangleAlert className="h-3.5 w-3.5 text-warning" aria-hidden />To check <span className="font-semibold tnum">{fmt(amber)}</span></li>
            <li className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px] bg-muted-foreground/40" aria-hidden />Clear <span className="font-semibold tnum">{fmt(clear)}</span></li>
          </ul>
        </div>

        {/* the reasons */}
        <div className="min-w-0 flex-1 lg:border-l lg:border-border/60 lg:pl-8">
          {slots === 0 ? (
            <div className="flex h-full min-h-[5rem] items-center gap-3 text-sm text-muted-foreground" data-testid={`${testid}-clear-note`}>
              <CircleCheck className="h-5 w-5 shrink-0 text-success" aria-hidden />
              No slot needs attention right now.
            </div>
          ) : top.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid={`${testid}-findings-only`}>
              {fmt(summary.findingsOnly)} slot{summary.findingsOnly === 1 ? ' has' : 's have'} settings to check. Open a slot's check to see which.
            </p>
          ) : (
            <>
              <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">Top reasons</p>
              <ol className="space-y-2.5" data-testid={`${testid}-reasons`}>
                {top.map((r) => (
                  <li key={r.key} className="min-w-0" data-testid={`${testid}-reason-${r.key}`}>
                    <Link to={to} className="group block min-w-0" title={r.text}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 truncate text-sm text-foreground/90 group-hover:text-foreground">{r.text}</span>
                        <span className="shrink-0 text-xs text-muted-foreground"><span className="font-semibold tnum text-foreground">{fmt(r.count)}</span> slot{r.count === 1 ? '' : 's'}</span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-secondary">
                        <div className={cn('h-full rounded-full', r.level === 'red' ? 'bg-danger' : 'bg-warning')}
                          style={{ width: `${maxCount ? Math.max(4, (r.count / maxCount) * 100) : 0}%` }} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
              {reasons.length > top.length && (
                <p className="mt-2 text-xs text-muted-foreground">+{reasons.length - top.length} more reason{reasons.length - top.length === 1 ? '' : 's'}</p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
